import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BodyClasses,
  ColumnDefinition,
  ColumnDisplayType,
  ColumnSizes,
  GridComponent,
  GridConfiguration,
  OrderByPipe,
  DialogContent
} from 'components';
import { DialogService } from 'src/app/core/services/dialog.service';
import { CodeWrapperService } from 'src/app/core/services/service-wrappers/code-wrapper.service';
import { PointOfOriginService } from './point-of-origin.service';
import { take, map } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { UploadCodesComponent } from '../upload-codes/upload-codes.component';
import { SessionStorageService } from '@core/services/session-storage.service';
import { ImplementationDetailViewModel, ImplementationViewModel, UserDetailsViewModel } from 'data';
import { PointOfOriginDialogComponent } from './point-of-origin-dialog/point-of-origin-dialog.component';
import * as ImplementationActions from '../../implementation/state/implementation.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { selectImplementation } from '@app/features/implementation/state/implementation.selectors';
import { ImplementationState } from '@app/features/implementation/state/implementation.state';
import { ImplementationWrapperService } from '@core/services/service-wrappers/implementation-wrapper.service';
import { Store } from '@ngrx/store';
import { Subscription, Observable } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-point-of-origin',
  templateUrl: './point-of-origin.component.html',
  styleUrls: ['./point-of-origin.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [PointOfOriginService]
})
export class PointOfOriginComponent {
  @ViewChild(GridComponent) grid: GridComponent;
  orderPipe: OrderByPipe = new OrderByPipe();
  activeFilter: string = 'active';
  userInfo: UserDetailsViewModel;
  viewOnly = false;
  viewOnlyOptions = [{ id: 'view', label: 'View' }];
  overflowMenuOptions = [
    {
      id: 'edit',
      label: 'Edit'
    }
  ];
  private columnDefinitions: ColumnDefinition[] = [
    {
      id: 'action',
      displayKey: 'action',
      headerText: '',
      displayType: ColumnDisplayType.actionList,
      columnSize: ColumnSizes.tiny,
      bodyClasses: [BodyClasses.alignRight],
      displayOrder: 0,
      overflowMenuOptions: this.overflowMenuOptions,
      overflowMenuClicked: (value, data) => {
        if (value === 'delete') {
          this.deleteEntry(data);
        } else if (value === 'edit') {
          this.openPointOfOriginModal(data.id);
        } else if (value === 'view') {
          this.openPointOfOriginModal(data.id, true);
        }
      }
    },
    {
      id: 'code',
      displayKey: 'code',
      headerText: 'Code',
      displayOrder: 1,
      columnSize: ColumnSizes.fixedColumn,
      filterable: true,
      sortable: true,
      sticky: true
    },
    {
      id: 'description',
      displayKey: 'name',
      headerText: 'Description',
      displayOrder: 2,
      columnSize: ColumnSizes.superWide,
      sortable: true,
      filterable: true
    }
  ];
  gridConfig: GridConfiguration = {
    columnDefinitions: this.columnDefinitions,
    displayColumns: this.orderPipe.transform(this.columnDefinitions, 'displayOrder').map((x) => x.id)
  };

  dataSource: PointOfOriginService;
  constructor(
    private pointOfOriginService: PointOfOriginService,
    public dialog: MatDialog,
    private deleteService: CodeWrapperService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private sessionStorageService: SessionStorageService,
    private activatedRoute: ActivatedRoute,
    private implementationService: ImplementationWrapperService,
    private store: Store<ImplementationState>,
    private router: Router,
  ) {
    this.userInfo = this.sessionStorageService.getItem('user-info');
    this.viewOnly =
      this.userInfo?.userPermissions?.find((x) => x.permissionType == 'SystemMaintenance')?.accessType !== 'Update';
    if (this.viewOnly) {
      this.columnDefinitions[0].overflowMenuOptions = this.viewOnlyOptions;
    }
    this.dataSource = pointOfOriginService;
    this.dataSource.activeFilter = 'active';
  }

  addPointOfOrigin() {
    this.openPointOfOriginModal(null);
  }

  openPointOfOriginModal(pointOfOriginId: string, viewOnly = false) {
    const dialog = this.dialog.open(PointOfOriginDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: { pointOfOriginId, viewOnly }
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.grid.loadData();
      }
    });
  }

  deleteEntry(data) {
    const modalContent: DialogContent = {
      header: 'Delete Point Of Origin',
      body: `Are you sure you want to delete "${data.name}"? This cannot be undone.`,
      cancelButtonText: 'Cancel',
      OKButtonText: 'Delete'
    };
    this.dialogService.showConfirm(modalContent).subscribe((result) => {
      if (result) {
        this.deleteService
          .apiV1CodeCodetypeDeleteIdDelete('PointOfOrigin', data.id)
          .pipe(
            map((x: any) => x),
            take(1)
          )
          .subscribe((deleteResult) => {
            this.grid.loadData();
            this.notificationService.notifyNow('Point Of Origin Deleted');
          });
      }
    });
  }

  uploadCodes() {
    const dialog = this.dialog.open(UploadCodesComponent, {
      disableClose: true,
      autoFocus: false,
      height: '600px',
      width: '600px',
      data: { codeType: 'PointOfOrigin' }
    });

    dialog.afterClosed().subscribe((result) => {
      this.grid.loadData();
    });
  }

  showInactiveChanges(event) {
    this.activeFilter = event.checked ? 'active' : 'inactive';
    this.dataSource.activeFilter = this.activeFilter;
    this.grid.loadData();
  }

  public isImplementation: boolean = false;
  public gonext: boolean = false;
  public itemCount: number = 0;
  private activatedRouteSub: Subscription;
  private itemCountSub: Subscription;
  private implementation$: Observable<ImplementationViewModel> = this.store.select(selectImplementation);
  private implementationSub: Subscription;
  private implementation: ImplementationViewModel = null;
  private implementationDetail: ImplementationDetailViewModel = null;
  private nextImplementationDetail: ImplementationDetailViewModel = null;
  private STEP_CODE = 'point-of-origin-codes';

  implementationSubscribe() {
    this.activatedRouteSub = this.activatedRoute.data?.subscribe((data) => {
      this.isImplementation = data.implementation;

      if (this.isImplementation) {
        this.itemCountSub = this.pointOfOriginService.itemCountSubject.subscribe((c) => (this.itemCount = c));
        this.implementationSub = this.implementation$.subscribe((i) => {
          this.implementation = i;
          this.implementationDetail = this.implementation.details.find(
            (d) => d.implementationStepCode == this.STEP_CODE
          );
          this.nextImplementationDetail = this.implementation.details.find(
            (d) => d.order == this.implementationDetail.order + 1
          );
        });
      }
    });
    this.store.dispatch(ImplementationActions.updateImplementationStep({ stepCode: this.STEP_CODE }));
  }

  implementationUnsubscribe() {
    if (this.isImplementation) {
      this.activatedRouteSub.unsubscribe();
      this.itemCountSub.unsubscribe();
      this.implementationSub.unsubscribe();
    }
  }

  next(): void {
    if (this.implementationDetail.status == 'InProgress') {
      this.gonext = true;
      this.implementationService.apiV1ImplementationCompleteStepCodePost(this.STEP_CODE).subscribe(
        () => {
          this.gonext = false;
          this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
        },
        () => (this.gonext = false)
      );
    } else {
      this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
    }
  }

  ngOnInit(): void {
    this.implementationSubscribe();
  }
  ngOnDestroy(): void {
    this.implementationUnsubscribe();
  }
}
